import React, { useState, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { DialogProps } from '@radix-ui/react-dialog';

import { queryClient } from '@api/queryClient';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@epoch-ui/components/Modal/modal';
import { SearchInput } from '@epoch-ui/components/SearchInput/SearchInput';
import { Button } from '@epoch-ui/components/Button';
import { SurveyTemplate, SurveyTemplateTypes } from 'types/survey_template.types';
import BackArrowIcon from 'components/common/icons/BackArrowIcon';
import { cn } from '@epoch-ui/lib/utils';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { useTemplatesQuery } from '@event-editor/hooks/useTemplatesQuery';
import { updateEventFeedbackTemplate } from '@event-editor/api/feedback/updateEventFeedbackTemplate';
import { updateEventQuestionnaireTemplate } from '@event-editor/api/questionnaire/updateEventQuestionnaireTemplate';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';

import { TemplateLoadingSkeleton } from '../TemplateLoadingSkeleton';
import { TemplateList } from '../TemplateList';
import { EmptyTemplateState } from '../EmptyTemplateState';
import { QuestionPreview } from '../QuestionPreview';
import { AddRequiredQuestionConfirmationDialog } from '../EditQuestionSideSheet/AddRequiredQuestionConfirmationDialog';

interface TemplateSelectionModalProps extends DialogProps {
  templateType: SurveyTemplateTypes;
}

/**
 * Modal for browsing and selecting templates for event questionnaires or feedback forms.
 * Allows users to search through available templates, preview their questions, and apply them to the current event.
 * When applying a questionnaire template with required questions to an event with confirmed guests,
 * displays a confirmation dialog warning that guest statuses will be changed to incomplete.
 *
 * @param open - Controls the visibility of the modal
 * @param onOpenChange - Callback fired when the modal open state changes
 * @param templateType - The type of template to display (FEEDBACK or EVENT_QUESTIONNAIRE)
 */
export const TemplateSelectionModal: React.FC<TemplateSelectionModalProps> = ({
  open,
  onOpenChange,
  templateType,
  ...props
}) => {
  const event = useEditableEvent();
  const [searchText, setSearchText] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<SurveyTemplate | null>(null);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [pendingTemplateId, setPendingTemplateId] = useState<number | null>(null);

  const confirmedGuests = event.guestListTotals?.confirmed ?? 0;

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } = useTemplatesQuery({
    searchText,
    enabled: open || false,
    type: templateType
  });

  const allTemplates = useMemo(() => {
    return data?.pages.flatMap((page) => page.templates) ?? [];
  }, [data?.pages]);

  const updateFeedbackTemplateMutation = useMutation({
    mutationFn: updateEventFeedbackTemplate,
    onSuccess: (result, variables) => {
      const { event: eventResult } = result.updateEventFeedbackTemplate;

      // Update the event query data in the cache with the new questions
      queryClient.setQueryData(eventEditorQueryKeys.event(variables.eventId), (oldData: any) =>
        oldData
          ? {
              event: {
                ...oldData.event,
                feedbackEnable: eventResult.feedbackEnable,
                isFeedbackAnonymous: eventResult.isFeedbackAnonymous,
                feedbackSchedule: eventResult.feedbackSchedule,
                feedbackNotificationChannel: eventResult.feedbackNotificationChannel,
                postQuestions: eventResult.postQuestions
              }
            }
          : oldData
      );
      onOpenChange?.(false);
    }
  });

  const updateQuestionnaireTemplateMutation = useMutation({
    mutationFn: updateEventQuestionnaireTemplate,
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({
        queryKey: eventEditorQueryKeys.event(variables.eventId)
      });
      // TODO: Invalidate the guest queries here once they are implemented
      onOpenChange?.(false);
    }
  });

  /**
   * Saves the template to the event by calling the appropriate mutation
   * based on the template type (feedback or questionnaire).
   */
  const saveTemplate = (templateId: number): void => {
    if (event.id) {
      if (templateType === SurveyTemplateTypes.FEEDBACK) {
        // Feedback form
        updateFeedbackTemplateMutation.mutate({
          eventId: event.id,
          templateId
        });
      } else {
        // Pre-event questionnaire
        updateQuestionnaireTemplateMutation.mutate({
          eventId: event.id,
          templateId
        });
      }
    }
  };

  /**
   * Handles the "Use template" button click.
   * Checks if confirmation is needed (for questionnaire templates with required questions
   * when there are confirmed guests), otherwise proceeds to save the template immediately.
   */
  const handleUseTemplate = (): void => {
    if (event.id && selectedTemplate) {
      const templateId = Number(selectedTemplate.id);

      // Check if template has required questions and there are confirmed guests (for questionnaire only)
      if (templateType === SurveyTemplateTypes.EVENT_QUESTIONNAIRE) {
        const hasRequiredQuestions = selectedTemplate.questions.some((q) => q.isRequired);

        if (confirmedGuests > 0 && hasRequiredQuestions) {
          setPendingTemplateId(templateId);
          setIsConfirmationOpen(true);
          return;
        }
      }

      saveTemplate(templateId);
    }
  };

  /**
   * Handles confirmation dialog's "Add question" button click.
   * Called after the user confirms they want to proceed with adding required questions
   * that will change confirmed guest statuses to incomplete.
   */
  const handleConfirmUseTemplate = (): void => {
    setIsConfirmationOpen(false);
    if (pendingTemplateId) {
      setPendingTemplateId(null);
      saveTemplate(pendingTemplateId);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          setSelectedTemplate(null);
        }
        onOpenChange?.(isOpen);
      }}
      {...props}
    >
      <ModalContent
        size="md"
        className={cn('tw-flex tw-flex-col', selectedTemplate ? 'tw-max-h-[80vh]' : 'tw-h-[80vh]')}
        data-testid="template-selection-modal"
      >
        <ModalHeader>
          <div className="tw-flex tw-items-center">
            {selectedTemplate && (
              <Button
                variant="ghost"
                onClick={() => setSelectedTemplate(null)}
                className="tw-mr-3 tw-h-6 tw-p-0"
              >
                <BackArrowIcon size="medium" />
              </Button>
            )}
            <ModalTitle
              className={cn({
                'tw-text-lg tw-font-medium tw-leading-6 tw-text-dark-black': selectedTemplate,
                'tw-mb-1': !selectedTemplate
              })}
            >
              {selectedTemplate
                ? selectedTemplate.title
                : `${
                    templateType === SurveyTemplateTypes.FEEDBACK
                      ? 'Feedback form'
                      : 'Event questionnaire'
                  } templates`}
            </ModalTitle>
          </div>
        </ModalHeader>
        <div className="tw-flex-1">
          {selectedTemplate ? (
            <>
              <div
                className={cn(
                  'tw-flex-1 tw-overflow-y-auto tw-overflow-x-hidden tw-rounded-lg tw-border tw-border-solid tw-border-border-grey tw-p-6',
                  '[&_*]:tw-pointer-events-none [&_*]:tw-cursor-default'
                )}
              >
                {selectedTemplate.questions.map((question) => (
                  <QuestionPreview key={question.id} question={question} />
                ))}
              </div>
              <div className="tw-mt-5 tw-flex tw-justify-end">
                <Button
                  onClick={handleUseTemplate}
                  disabled={
                    updateFeedbackTemplateMutation.isPending ||
                    updateQuestionnaireTemplateMutation.isPending
                  }
                >
                  Use template
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="tw-mb-6">
                <SearchInput
                  placeholder="Search "
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="tw-w-full"
                />
              </div>
              <div id="template-list-container" className="tw-flex-1 tw-overflow-y-auto">
                {isLoading && allTemplates.length === 0 && <TemplateLoadingSkeleton />}
                {!isLoading && allTemplates.length === 0 && (
                  <EmptyTemplateState searchText={searchText} />
                )}
                {!isLoading && allTemplates.length > 0 && (
                  <TemplateList
                    templates={allTemplates}
                    hasNextPage={hasNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    onSelectTemplate={setSelectedTemplate}
                    onFetchNextPage={fetchNextPage}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </ModalContent>

      {/* Confirmation dialog for adding required questions */}
      <AddRequiredQuestionConfirmationDialog
        open={isConfirmationOpen}
        onOpenChange={setIsConfirmationOpen}
        onConfirm={handleConfirmUseTemplate}
      />
    </Modal>
  );
};
